import { useLocation } from '@common/location-layer/composables/useLocation'

interface InitLocationDeps {
  initLocation: () => Promise<void>
  store: { hasLocation: boolean; applyBranding: () => void }
  isClient: boolean
}

/**
 * Bootstrap the location store and, on the client, re-apply branding.
 *
 * `applyBranding` only takes effect in the browser and its only client trigger is
 * a fresh `fetchLocation`. When SSR already populated the store, the client-tick
 * `initLocation` short-circuits on `hasLocation`, so no client fetch — and no
 * `applyBranding` — runs, leaving brand-colour CSS vars (and favicon/title on
 * non-branded-head apps) unapplied. Re-apply here on the client when the store is
 * already populated. `applyBranding` is idempotent and no-ops on the server.
 */
export async function runInitLocation({ initLocation, store, isClient }: InitLocationDeps): Promise<void> {
  await initLocation()

  if (isClient && store.hasLocation) {
    store.applyBranding()
    return
  }

  // Diagnostic only — surfaces the rare "neither SSR nor client could populate
  // the store" case to anyone tailing logs. Not a fatal condition.
  if (isClient && !store.hasLocation) {
    console.warn('[Init-Location] location data still empty after init; portal branding may be missing')
  }
}

/**
 * Location initialization plugin - shared across all apps
 * Initializes location store on server side. When SSR cannot resolve the host
 * (e.g. health-check, prerender, or hook-timing failure), useLocation returns
 * null and leaves the store empty — this plugin runs again on the client tick
 * (mode: 'all') and the !hasLocation guard inside initLocation triggers a real
 * client-side fetch with window.location.hostname.
 */
export default defineNuxtPlugin(async () => {
  try {
    const { initLocation, store } = useLocation()
    await runInitLocation({ initLocation, store, isClient: Boolean(process.client) })
  } catch (error) {
    // Never block app start due to bootstrap; log for diagnostics
    console.error('[Init-Location] Location initialization failed:', error)
  }
})
